'use client'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Image from 'next/image'
import React from 'react'
import useAuth from '../hooks/useAuth'
import BrandSwitcher from './brand/BrandSwitcher'
import SidebarItems from './SidebarItems'

// Use string path instead of static import to avoid Sharp issues during Docker build
const Logo = '/assets/images/WH-logo.png'

interface SidebarProps {
  isCollapsed: boolean
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, setIsCollapsed }) => {
  const theme = useTheme()
  const { logout, effectivePartnerType, user, isLoading } = useAuth()
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Load initial collapsed state from localStorage
  React.useEffect(() => {
    try {
      const saved = localStorage.getItem('main-sidebar-collapsed')
      if (saved !== null) {
        setIsCollapsed(JSON.parse(saved))
      }
    } catch (error: any) {
      console.warn('Failed to load main sidebar state:', error)
    }
  }, [setIsCollapsed])

  // Keep sidebar visible while auth context resolves partner type;
  // only block when loading is complete and user is still missing.
  if (!isLoading && !user) return null

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev)
  }

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const newValue = !prev
      // Save to localStorage for AdminSidebar to read
      try {
        localStorage.setItem('main-sidebar-collapsed', JSON.stringify(newValue))
      } catch (error: any) {
        console.warn('Failed to save main sidebar state:', error)
      }
      return newValue
    })
  }

  const handleLogout = async () => {
    await logout()
  }

  const handleCloseOnClick = () => {
    if (isMobile) {
      setIsDrawerOpen(false)
    }
  }

  const sidebarContent = (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        padding: 1,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: isCollapsed ? 80 : 250,
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000,
        borderRight: `1px solid ${theme.palette.divider}`,
        transition: 'width 0.3s ease',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {!isCollapsed && (
          <Image src={Logo} alt="logo" width={150} height={25} />
        )}
      </Box>

      {/* Sidebar Items */}
      <SidebarItems
        isCollapsed={isCollapsed}
        onItemClick={handleCloseOnClick}
      />

      {/* Footer Items */}
      <Box
        sx={{
          borderTop: `2px solid ${theme.palette.divider}`,
          paddingTop: 1,
          marginTop: 'auto',
        }}
      >
        <List>
          <BrandSwitcher isCollapsed={isCollapsed} />
          {/* ✅ Hide User Settings if effectivePartnerType is 'Demo' */}
          {effectivePartnerType !== 'Demo' && (
            <ListItemButton
              component="a"
              href="/settings"
              onClick={handleCloseOnClick}
              sx={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}
            >
              <ListItemIcon
                sx={{
                  justifyContent: 'center',
                  minWidth: isCollapsed ? 0 : 48,
                }}
              >
                <SettingsIcon sx={{ fontSize: isCollapsed ? 24 : 20 }} />
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary="User Settings" />}
            </ListItemButton>
          )}

          <ListItemButton
            onClick={handleLogout}
            sx={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}
          >
            <ListItemIcon
              sx={{ justifyContent: 'center', minWidth: isCollapsed ? 0 : 48 }}
            >
              <LogoutIcon sx={{ fontSize: isCollapsed ? 24 : 20 }} />
            </ListItemIcon>
            {!isCollapsed && <ListItemText primary="Log out" />}
          </ListItemButton>
        </List>
      </Box>

      {/* Collapse/Expand Toggle */}
      {!isMobile && (
        <Box
          onClick={toggleCollapse}
          sx={{
            position: 'absolute',
            top: '50%',
            left: isCollapsed ? '80px' : '250px',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '100px',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            zIndex: 1200,
          }}
        >
          {isCollapsed ? (
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          ) : (
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          )}
        </Box>
      )}
    </Box>
  )

  return (
    <>
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1200,
            padding: 1,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <IconButton
            edge="start"
            onClick={toggleDrawer}
            sx={{
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            {isDrawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
      )}

      <Drawer
        anchor="left"
        open={isDrawerOpen}
        onClose={toggleDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: '250px',
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {!isMobile && sidebarContent}
    </>
  )
}

export default Sidebar
